import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileDown, Loader2, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLANK = {
  first_name: '', last_name: '', dob: '',
  street_address: '', apt_suite: '',
  city: '', state: '', zip: '', ssn: '',
};

/**
 * Lightweight modal that lets a broker/UW type in any owner's info and
 * download a Jumbo Term Loan application PDF where only the OWNER INFO
 * section reflects that person. Nothing is saved.
 */
const JumboOwnerOverrideDialog = ({ open, onOpenChange, applicationId, authToken }) => {
  const [form, setForm] = useState(BLANK);
  const [showSsn, setShowSsn] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const reset = () => setForm(BLANK);

  const handleDownload = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) {
      toast.error('Enter at least the owner\'s first or last name.');
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/pdf/jumbo-term-loan/owner-override`,
        form,
        {
          headers: { Authorization: `Bearer ${authToken}` },
          responseType: 'blob',
        }
      );
      // Extract filename from Content-Disposition
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] :
        `Quidity_Term_Loan_${(form.first_name || '').replace(/\s+/g, '_')}_${(form.last_name || '').replace(/\s+/g, '_')}.pdf`;

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success(`Downloaded application for ${form.first_name || ''} ${form.last_name || ''}`.trim());
      onOpenChange(false);
      reset();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Download failed';
      toast.error(typeof detail === 'string' ? detail : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid="jumbo-owner-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-teal-600" />
            Download Application for a Different Owner
          </DialogTitle>
          <DialogDescription>
            Type this owner&apos;s info below. The rest of the application (business
            info, amount, etc.) stays the same. Nothing is saved &mdash; this just
            generates a fresh PDF for this owner to sign.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Legal First Name</Label>
            <Input
              value={form.first_name}
              onChange={e => set('first_name', e.target.value)}
              className="h-9"
              data-testid="owner-override-first-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Legal Last Name</Label>
            <Input
              value={form.last_name}
              onChange={e => set('last_name', e.target.value)}
              className="h-9"
              data-testid="owner-override-last-name"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">DOB (MM/DD/YYYY)</Label>
            <Input
              value={form.dob}
              onChange={e => set('dob', e.target.value)}
              placeholder="MM/DD/YYYY"
              className="h-9"
              data-testid="owner-override-dob"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Home Street Address</Label>
            <Input
              value={form.street_address}
              onChange={e => set('street_address', e.target.value)}
              className="h-9"
              data-testid="owner-override-street"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Apt / Suite</Label>
            <Input
              value={form.apt_suite}
              onChange={e => set('apt_suite', e.target.value)}
              className="h-9"
              data-testid="owner-override-apt"
            />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">City</Label>
            <Input
              value={form.city}
              onChange={e => set('city', e.target.value)}
              className="h-9"
              data-testid="owner-override-city"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">State</Label>
            <Input
              value={form.state}
              onChange={e => set('state', e.target.value)}
              className="h-9"
              data-testid="owner-override-state"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">ZIP</Label>
            <Input
              value={form.zip}
              onChange={e => set('zip', e.target.value)}
              className="h-9"
              data-testid="owner-override-zip"
            />
          </div>

          <div className="sm:col-span-3">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Social Security #</Label>
            <div className="relative">
              <Input
                type={showSsn ? 'text' : 'password'}
                value={form.ssn}
                onChange={e => set('ssn', e.target.value)}
                placeholder="XXX-XX-XXXX"
                className="h-9 pr-14"
                data-testid="owner-override-ssn"
              />
              <button
                type="button"
                onClick={() => setShowSsn(!showSsn)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-500 hover:text-slate-700"
              >
                {showSsn ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); reset(); }}
            disabled={downloading}
            data-testid="owner-override-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="owner-override-download"
          >
            {downloading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><FileDown className="h-4 w-4 mr-2" /> Download PDF</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JumboOwnerOverrideDialog;
